import React, {useCallback, useMemo} from "react";
import {streamFromService} from "@breachrx/streaming";
import {useAuth} from "../components/authentication/Context/AuthProvider";
import {uuidv7} from "uuidv7";
import {map} from "underscore";

const getRexAIBaseURL = () => {
  return process.env.REXAI_API || "https://localhost:8080/rexai";
};

const buildResponseError = (body, status, fallback) => {
  const error = new Error(body.message || body.error || fallback);
  error.code = body.code || body.error;
  error.status = status;
  error.details = body;
  return error;
};

export const useAIServices = () => {
  const {authSession, orgName} = useAuth();

  const headers = useCallback(
    (contentType) => {
      const contentTypeHeader = contentType ? {"Content-Type": contentType} : undefined;
      return {
        ...contentTypeHeader,
        Authorization: `Bearer ${authSession.IdToken}`,
        orgname: orgName,
        "X-Client-Platform": "web",
        "X-Authz-Type": "permission",
        "X-Request-ID": uuidv7(),
      };
    },
    [authSession, orgName],
  );

  const apiClient = useMemo(() => {
    const createMethod =
      (method) =>
        async (path, body, options = {}) => {
          const {signal, timeout = 30000, keepalive} = options;

          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), timeout);

          try {
            const response = await fetch(`${getRexAIBaseURL()}${path}`, {
              method,
              headers: headers("application/json"),
              body: body ? JSON.stringify(body) : undefined,
              signal: signal || controller.signal,
              ...(keepalive && {keepalive: true}),
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
              const errorBody = await response.json().catch(() => ({}));
              throw buildResponseError(
                errorBody,
                response.status,
                `Please try again later (status: ${response.status})`,
              );
            }

            return response.json();
          } catch (error) {
            clearTimeout(timeoutId);

            throw error.name === "AbortError" ?
              new Error("Request timed out, please try again later.") :
              error;
          }
        };

    return {
      post: createMethod("POST"),
      delete: createMethod("DELETE"),
      uploadDocument: async ({file, sessionId, email, documentUploadEnabled, signal}) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("sessionId", sessionId);
        formData.append("email", email);
        formData.append("documentUploadEnabled", documentUploadEnabled);

        const response = await fetch(`${getRexAIBaseURL()}/v1/maestro/upload`, {
          method: "POST",
          headers: headers(),
          body: formData,
          signal,
        });

        if (!response.ok) {
          const errorBody = await response.json().catch(() => ({}));
          throw buildResponseError(errorBody, response.status, "Upload failed");
        }

        return response.json();
      },
      streamTrustedAdvisor: async function* (request, options = {}) {
        const {
          prompt,
          email,
          messageId,
          sessionId,
          agentTaskId,
          allowInternetAccess,
          attachments,
          docIds,
          modelProfile,
          responseWorkflowName,
          skipReadOnlyConfirmation,
          timezone,
          trustedAdvisorDocumentUpload,
          trustedAdvisorPlatformTools,
          trustedAdvisorDocumentGeneration,
          orgMemoryEnabled,
          userMemoryOrgEnabled,
          wantsUserMemoryDisabled,
          userAgent,
          userName,
          messageContext,
          formToolsEnabled,
          allowedUiFormFields,
          allowedListTools,
          widgetType,
          widgetValue,
          widgetLabel,
        } = request;

        yield* streamFromService(
          `${getRexAIBaseURL()}/v1/maestro/stream`,
          {
            prompt,
            email,
            messageId,
            sessionId,
            agentTaskId,
            allowInternetAccess,
            ...(docIds?.length > 0 && {docIds}),
            ...(attachments?.length > 0 && {
              attachments: map(attachments, (attachment) => ({
                docId: attachment.docId,
                fileName: attachment.fileName,
                pageCount: attachment.pageCount,
              })),
            }),
            modelProfile,
            responseWorkflowName,
            skipReadOnlyConfirmation,
            timezone,
            trustedAdvisorDocumentUpload,
            trustedAdvisorPlatformTools,
            trustedAdvisorDocumentGeneration,
            orgMemoryEnabled,
            userMemoryOrgEnabled,
            wantsUserMemoryDisabled,
            userAgent,
            userName,
            messageContext,
            formToolsEnabled,
            allowedUiFormFields,
            allowedListTools,
            ...(widgetType && {widgetType, widgetValue, widgetLabel}),
          },
          {
            signal: options.signal,
            headers: headers("application/json"),
          },
        );
      },
    };
  }, [headers]);

  return {apiClient};
};
